import PropTypes from 'prop-types';
import { Modal, Steps, Progress, Button } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import useQuotesContext from './QuotesContext';
import useAuthContext from '../../contexts/AuthContext';
import useErrorMessage from '../../utils/ErrorMessage';
import GearsAnim from '../../assets/images/icons/rouages-animes.svg';
import CheckImg from '../../assets/images/icons/loader-check.svg';
import ErrorImg from '../../assets/images/icons/loader-error.svg';

const COLORS = {
  success: 'rgb(49, 188, 46)',
  error: 'rgb(161, 38, 38)',
  primary: { start: '#305181', end: '#2399dc' }
};

const MESSAGES = {
  financial: 'Situation Financière',
  geoRisks: 'Situation géographique GÉORISKS',
  tracfin: 'Situation Tracfin',
  tracfinError:
    'Situation Tracfin - Votre client(e) semble présent(e) dans les registres Tracfin',
  blacklist: 'Situation Blacklist Assurmax',
  blacklistError:
    'Situation Blacklist - Votre client(e) semble être blacklisté(e)',
  analyzing: 'Analyse de votre demande en cours...',
  successTitle: 'Votre enregistrement a été validé avec succès.',
  exemptionTitle: 'Devis mis en dérogation...',
  waiting: 'Merci de patienter...',
  errorTitle:
    'Votre client(e) semble présent(e) dans les registres Tracfin, nous vous invitons à contacter nos services au 03 28 64 96 23'
};

const createProgressIcon = (percent) => (
  <Progress
    type="circle"
    percent={percent}
    strokeColor={{ '0%': COLORS.primary.start, '100%': COLORS.primary.end }}
    strokeWidth={12}
    size={75}
    strokeLinecap="square"
    railColor="#e3e3e3"
  />
);

const createTitle = (text, color) => <span style={{ color }}>{text}</span>;

const createImageIcon = (src, alt) => (
  <div
    style={{
      width: 75,
      height: 75,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}
  >
    <img src={src} alt={alt} style={{ width: 75, height: 75 }} />
  </div>
);

const initialState = [
  {
    id: 'financial',
    content: MESSAGES.financial,
    icon: createProgressIcon(0)
  },
  {
    id: 'geoRisks',
    content: MESSAGES.geoRisks,
    icon: createProgressIcon(0)
  },
  {
    id: 'tracfin',
    content: MESSAGES.tracfin,
    icon: createProgressIcon(0)
  },
  {
    id: 'blacklist',
    content: MESSAGES.blacklist,
    icon: createProgressIcon(0)
  }
];

const StateDisplay = ({ state, onClose }) => {
  const displays = {
    progress: (
      <div
        style={{ display: 'flex', flexFlow: 'column', alignItems: 'center' }}
      >
        <img src={GearsAnim} alt="Analyse en cours" style={{ height: 120 }} />
        <h1>{MESSAGES.analyzing}</h1>
      </div>
    ),
    success: (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '32px 24px',
          gap: '16px'
        }}
      >
        <img src={CheckImg} alt="Validé" style={{ height: 100 }} />
        <h1
          style={{
            color: COLORS.success,
            textAlign: 'center',
            margin: 0
          }}
        >
          {MESSAGES.successTitle}
        </h1>
        <LoadingOutlined style={{ color: COLORS.success, fontSize: 24 }} />
        <h2
          style={{
            color: COLORS.success,
            textAlign: 'center',
            margin: 0,
            fontWeight: 'normal'
          }}
        >
          {MESSAGES.waiting}
        </h2>
      </div>
    ),
    tracfinError: (
      <div
        style={{
          display: 'flex',
          flexFlow: 'column',
          alignItems: 'center',
          padding: '24px 0'
        }}
      >
        <img src={CheckImg} alt="Validé" style={{ height: 100 }} />
        <h1 style={{ width: 350, color: COLORS.success }}>
          {MESSAGES.successTitle}
        </h1>
        <h1 style={{ width: 350, color: COLORS.error }}>
          {MESSAGES.exemptionTitle}
        </h1>
        <LoadingOutlined style={{ color: COLORS.success }} />
        <h2 style={{ width: 350, color: COLORS.success, textAlign: 'center' }}>
          {MESSAGES.waiting}
        </h2>
      </div>
    ),
    error: (
      <div
        style={{
          display: 'flex',
          flexFlow: 'column',
          alignItems: 'center',
          padding: '24px 0'
        }}
      >
        <img src={ErrorImg} alt="Echec" style={{ height: 100 }} />
        <h1 style={{ width: 350, color: COLORS.error }}>
          {MESSAGES.errorTitle}
        </h1>
        <Button type="primary" onClick={onClose}>
          Voir mon enregistrement
        </Button>
      </div>
    )
  };

  return displays[state] || null;
};

StateDisplay.propTypes = {
  state: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired
};

export const AnalyzeModal = ({
  createQuote,
  handleSubmit,
  type,
  tracfinOverride
}) => {
  const { dispatchAPI, user } = useAuthContext();
  const { message } = useErrorMessage();
  const {
    analyzeModalVisible,
    setAnalyzeModalVisible,
    validateQuote,
    formValues,
    selectedQuote
  } = useQuotesContext();
  const [steps, setSteps] = useState(initialState);
  const [generalState, setGeneralState] = useState('progress');

  const timeOut = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const updateStep = (stepIndex, updates) => {
    setSteps((prevSteps) =>
      prevSteps.map((s, idx) => (idx === stepIndex ? { ...s, ...updates } : s))
    );
  };

  const animateProgress = async (stepIndex, percentages) => {
    for (const percent of percentages) {
      await timeOut(150);
      updateStep(stepIndex, { icon: createProgressIcon(percent) });
    }
  };

  const validateStep = (stepIndex, message, hasError = false) => {
    updateStep(stepIndex, {
      title: createTitle(message, hasError ? COLORS.error : COLORS.success),
      icon: createImageIcon(
        hasError ? ErrorImg : CheckImg,
        hasError ? 'Echec' : 'Validé'
      )
    });
  };

  const checkTracfin = async () => {
    const body = createQuote
      ? formValues
      : { ...selectedQuote, needsToFind: true };
    const { data } = await dispatchAPI('POST', {
      url: `tracfin/${user?._id}?precise=true`,
      body
    });

    if (data.length && !tracfinOverride) {
      validateStep(2, MESSAGES.tracfinError, true);
      setGeneralState('error');
      return { hasError: true, data };
    }

    validateStep(
      2,
      data.length ? MESSAGES.tracfinError : MESSAGES.tracfin,
      data.length
    );
    return { hasError: false, data };
  };

  const checkBlacklist = async () => {
    if (!type) return false;
    const typeUpperCase = type.toUpperCase();
    const body = createQuote
      ? { ...formValues, type: typeUpperCase }
      : { ...selectedQuote, needsToFind: true };

    const { data: blackListData } = await dispatchAPI('POST', {
      url: 'blackList/check',
      body
    });

    if (blackListData) {
      validateStep(3, MESSAGES.blacklistError, true);
      setGeneralState('error');
      return true;
    }

    validateStep(3, MESSAGES.blacklist, false);
    return false;
  };

  useEffect(() => {
    if (!analyzeModalVisible) {
      setSteps(initialState);
      return;
    }

    const runAnalysis = async () => {
      try {
        await animateProgress(0, [26, 58, 72, 94]);
        validateStep(0, MESSAGES.financial);

        await animateProgress(1, [5, 26, 58, 72, 94]);
        validateStep(1, MESSAGES.geoRisks);

        await animateProgress(2, [5, 36, 63, 88]);
        const { hasError: tracfinError, data: tracfinData } =
          await checkTracfin();

        if (tracfinError) return;

        await animateProgress(3, [12, 48, 79]);
        await timeOut(450);

        const hasBlacklistError = await checkBlacklist();

        if (hasBlacklistError) return;

        if (tracfinData.length) {
          setGeneralState('tracfinError');
          await timeOut(2500);
          if (createQuote) await handleSubmit(true);
          else await validateQuote();
        } else {
          await timeOut(150);
          setGeneralState('success');
          if (createQuote) await handleSubmit();
          else await validateQuote();
        }
      } catch (e) {
        message(e);
      }
    };

    runAnalysis();
  }, [analyzeModalVisible]);

  return (
    <Modal
      open={analyzeModalVisible}
      width="70%"
      style={{ minWidth: 600, maxWidth: 800 }}
      footer={false}
      closable={false}
      maskClosable={false}
    >
      <StateDisplay
        state={generalState}
        onClose={() => setAnalyzeModalVisible(false)}
      />
      {generalState !== 'success' && (
        <Steps
          orientation="horizontal"
          titlePlacement="vertical"
          items={steps.map((step) => ({
            icon: step.icon,
            title: (
              <div style={{ marginTop: '20%' }}>
                {step.title || step.content}
              </div>
            )
          }))}
          style={{
            marginTop: 24
          }}
        />
      )}
    </Modal>
  );
};

AnalyzeModal.propTypes = {
  createQuote: PropTypes.bool,
  handleSubmit: PropTypes.func,
  type: PropTypes.string,
  tracfinOverride: PropTypes.bool
};
